import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useAuth } from '../pages/AuthContext';
import "../styles/Purchases.css";
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    faUndo, faSearch, faFileInvoice, faCalendarAlt, faUser, faPhone,
    faMapMarkerAlt, faBox, faTag, faBarcode, faRupeeSign, faCalculator,
    faExclamationTriangle, faTrash, faSave, faTimes, faSpinner,
    faCheckCircle, faExclamationCircle, faReceipt, faBoxes, faHistory,
    faPrint, faPalette, faLayerGroup, faClock, faLayerGroup as faRolls
} from '@fortawesome/free-solid-svg-icons';

// ==============================================
// Translation Utility
// ==============================================
const createFabReturnTranslator = () => {
    let cache = new Map();
    let translationListeners = new Set();

    const loadFromCache = () => {
        try {
            const cached = localStorage.getItem('ourzest_fab_return_translations');
            if (cached) {
                const parsed = JSON.parse(cached);
                Object.entries(parsed).forEach(([key, value]) => {
                    cache.set(key, value);
                });
            }
        } catch (e) {
            console.log("No cache found in Fab Return Form");
        }
    };

    const saveToCache = () => {
        try {
            const cacheObj = {};
            cache.forEach((value, key) => {
                cacheObj[key] = value;
            });
            localStorage.setItem('ourzest_fab_return_translations', JSON.stringify(cacheObj));
        } catch (e) {
            console.error("Error saving to cache:", e);
        }
    };

    const translateAPI = async (text, targetLang) => {
        if (targetLang === 'en') return text;

        try {
            const response = await fetch('https://translate.argosopentech.com/translate', {
                method: 'POST',
                body: JSON.stringify({ q: text, source: 'en', target: targetLang }),
                headers: { 'Content-Type': 'application/json' }
            });

            if (response.ok) {
                const data = await response.json();
                return data.translatedText;
            }
        } catch (e) {
            console.log("LibreTranslate failed, trying MyMemory");
        }

        try {
            const response = await fetch(
                `https://api.mymemory.translated.net/get?q=${encodeURIComponent(text)}&langpair=en|${targetLang}`
            );
            if (response.ok) {
                const data = await response.json();
                return data.responseData.translatedText;
            }
        } catch (e) {
            console.log("MyMemory translation failed");
        }

        return text;
    };

    const translateText = async (text, targetLang) => {
        const cacheKey = `${text}_${targetLang}`;

        if (cache.has(cacheKey)) {
            return cache.get(cacheKey);
        }

        const translated = await translateAPI(text, targetLang);
        cache.set(cacheKey, translated);
        saveToCache();

        return translated;
    };

    const defaultTexts = {
        processFabricReturn: 'Process Fabric Return',
        returnFabricItems: 'Search invoice and return fabric items',
        returnInformation: 'Return Information',
        returnNumber: 'Return Number',
        returnDate: 'Return Date',
        originalInvoice: 'Original Invoice',
        originalBillNumber: 'Original Bill Number',
        originalDate: 'Original Date',
        customerDetails: 'Customer Details',
        customerName: 'Customer Name',
        customerPhone: 'Phone Number',
        customerGST: 'GST Number',
        searchInvoice: '',
        searchPlaceholder: 'Enter Invoice',
        invoiceFound: 'Invoice found',
        invoiceNotFound: 'Invoice not found or no items available for return',
        itemsToReturn: 'Items to Return',
        productName: 'Product',
        colorCode: 'Color',
        batchNumber: 'Batch',
        category: 'Category',
        categoryType: 'Type',
        unit: 'Unit',
        price: 'Price',
        quantityToReturn: 'Qty to Return',
        rollsToReturn: 'Rolls to Return',
        available: 'Available',
        availableRolls: 'Available Rolls',
        total: 'Total',
        remove: 'Remove',
        returnCalculations: 'Return Calculations',
        subtotal: 'Subtotal',
        discountPercent: 'Discount %',
        discountAmount: 'Discount Amount',
        taxPercent: 'Tax %',
        taxAmount: 'Tax Amount',
        grandTotal: 'Grand Total',
        refundAmount: 'Refund Amount',
        refundMethod: 'Refund Method',
        cash: 'Cash',
        card: 'Card',
        upi: 'UPI',
        bankTransfer: 'Bank Transfer',
        wallet: 'Wallet',
        returnReason: 'Return Reason',
        notes: 'Notes',
        processReturnBtn: 'Process Return',
        clear: 'Clear',
        saving: 'Processing...',
        loading: 'Loading...',
        returnProcessed: 'Fabric return processed successfully!',
        failedProcess: 'Failed to process return',
        networkError: 'Network error occurred',
        noShopSelected: 'No shop selected. Please select a shop first.',
        itemsRequired: 'At least one item is required for return',
        quantityExceedsAvailable: 'Quantity exceeds available stock for return',
        rollsExceedsAvailable: 'Rolls exceed available stock for return',
        invalidQuantity: 'Please enter a valid quantity',
        invalidRolls: 'Please enter a valid number of rolls',
        returnReasonRequired: 'Return reason is required',
        success: 'Success',
        error: 'Error',
        warning: 'Warning',
        todayReturns: "Today's Returns",
        totalReturns: 'Total Returns',
        totalAmount: 'Total Amount',
        totalRefund: 'Total Refund',
        printReceipt: 'Print Receipt',
        returnReceipt: 'FABRIC RETURN RECEIPT',
        thankYou: 'Thank you for your business!',
        poweredBy: 'Powered by OurZest',
        itemsReturned: 'Items Returned',
        close: 'Close',
        print: 'Print',
        yards: 'Yards',
        yardsPerRoll: 'Yards/Roll',
        totalYards: 'Total Yards',
        date: 'Date',
        time: 'Time',
        cashier: 'Cashier',
        totalQuantity: 'Total Quantity',
        totalYardsSummary: 'Total Yards',
        returnTotal: 'Return Total',
        page: 'Page',
        rolls: 'Rolls'
    };

    const getTranslations = async (lang) => {
        if (lang === 'en') {
            return defaultTexts;
        }

        const translations = {};
        const promises = [];

        Object.keys(defaultTexts).forEach(key => {
            const cacheKey = `${defaultTexts[key]}_${lang}`;
            if (cache.has(cacheKey)) {
                translations[key] = cache.get(cacheKey);
            } else {
                promises.push(
                    translateText(defaultTexts[key], lang).then(translated => {
                        translations[key] = translated;
                    })
                );
            }
        });

        if (promises.length > 0) {
            await Promise.all(promises);
            saveToCache();
        }

        return translations;
    };

    const addListener = (callback) => {
        translationListeners.add(callback);
    };

    const removeListener = (callback) => {
        translationListeners.delete(callback);
    };

    const notifyListeners = (lang) => {
        translationListeners.forEach(callback => callback(lang));
    };

    const checkLanguageChange = () => {
        const currentLang = localStorage.getItem('ourzest_language') || 'en';
        notifyListeners(currentLang);
    };

    const startLanguageCheck = () => {
        checkLanguageChange();
        const intervalId = setInterval(checkLanguageChange, 500);
        window.addEventListener('storage', checkLanguageChange);
        return () => {
            clearInterval(intervalId);
            window.removeEventListener('storage', checkLanguageChange);
        };
    };

    loadFromCache();

    return {
        getTranslations,
        translateText,
        addListener,
        removeListener,
        startLanguageCheck
    };
};

const fabReturnTranslator = createFabReturnTranslator();

// ==============================================
// Toast Notification Component
// ==============================================
const ToastNotification = ({ toast, setToast, translations }) => {
    const getIconForType = (type) => {
        switch (type) {
            case 'success': return faCheckCircle;
            case 'error': return faExclamationCircle;
            case 'warning': return faExclamationTriangle;
            default: return faCheckCircle;
        }
    };

    const getTitleForType = (type) => {
        switch (type) {
            case 'success': return translations?.success || 'Success';
            case 'error': return translations?.error || 'Error';
            case 'warning': return translations?.warning || 'Warning';
            default: return '';
        }
    };

    if (!toast.show) return null;

    const opacity = Math.max(toast.progress / 100, 0.1);

    return (
        <div className={`toast-notification ${toast.type} ${toast.direction === 'rtl' ? 'toast-rtl' : 'toast-ltr'}`} style={{ opacity }}>
            <div className="toast-content">
                <div className="toast-icon">
                    <FontAwesomeIcon icon={getIconForType(toast.type)} />
                </div>
                <div className="toast-text">
                    <div className="toast-title">{getTitleForType(toast.type)}</div>
                    <div className="toast-message">{toast.message}</div>
                </div>
                <button className="toast-close" onClick={() => setToast(prev => ({ ...prev, show: false }))}>
                    <FontAwesomeIcon icon={faTimes} />
                </button>
            </div>
            <div className="toast-timer">
                <div className="toast-progress-bar" style={{ width: `${toast.progress}%`, transition: 'width 0.1s linear' }}></div>
                <div className="toast-countdown">{toast.timeLeft}s</div>
            </div>
        </div>
    );
};

// ==============================================
// Fab Return Items Table Component with ROLLS
// ==============================================
const FabReturnItemsTable = ({
    items,
    onQuantityChange,
    onRollsChange,
    onRemove,
    errors,
    disabled,
    translations,
    isRTL
}) => {
    const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);

    useEffect(() => {
        const handleResize = () => {
            setIsMobile(window.innerWidth <= 768);
        };

        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    const calculateTotal = (price, quantity) => {
        const p = parseFloat(price) || 0;
        const q = parseFloat(quantity) || 0;
        return (p * q).toFixed(2);
    };

    const getSafeAvailable = (item) => {
        const available = parseFloat(item.available_for_return);
        return !isNaN(available) && available > 0 ? available : 0;
    };

    const getSafeAvailableRolls = (item) => {
        const available = parseFloat(item.available_rolls);
        return !isNaN(available) && available > 0 ? available : 0;
    };

    if (isMobile) {
        return (
            <div className="items-mobile-container">
                <div className="items-header-mobile">
                    <h4>
                        <FontAwesomeIcon icon={faBoxes} className="header-icon-only" />
                        {translations?.itemsToReturn || 'Items to Return'}
                    </h4>
                </div>
                <div className="items-cards-container">
                    {items.map((item, index) => {
                        const availableQty = getSafeAvailable(item);
                        const availableRolls = getSafeAvailableRolls(item);
                        return (
                            <div key={index} className="item-card-mobile">
                                <div className="item-card-header">
                                    <span className="item-number">
                                        <FontAwesomeIcon icon={faBox} className="header-icon-only" />
                                        {item.product_name}
                                    </span>
                                    <button
                                        type="button"
                                        className="item-remove-btn-mobile"
                                        onClick={() => onRemove(index)}
                                        disabled={disabled}
                                    >
                                        <FontAwesomeIcon icon={faTrash} />
                                    </button>
                                </div>

                                <div className="item-card-content">
                                    {item.product_colorcode && (
                                        <div className="mobile-field">
                                            <label>
                                                <FontAwesomeIcon icon={faPalette} className="field-icon" />
                                                {translations?.colorCode || 'Color'}
                                            </label>
                                            <input type="text" value={item.product_colorcode} readOnly className="readonly-input" />
                                        </div>
                                    )}

                                    <div className="mobile-field">
                                        <label>
                                            <FontAwesomeIcon icon={faTag} className="field-icon" />
                                            {translations?.batchNumber || 'Batch'}
                                        </label>
                                        <input type="text" value={item.batch_no || ''} readOnly className="readonly-input" />
                                    </div>

                                    <div className="mobile-field">
                                        <label>{translations?.unit || 'Unit'}</label>
                                        <input type="text" value={item.unit} readOnly className="readonly-input" />
                                    </div>

                                    <div className="mobile-row">
                                        <div className={`mobile-field half ${errors[`quantity_${index}`] ? 'has-error' : ''}`}>
                                            <label>{translations?.quantityToReturn || 'Qty to Return'} *</label>
                                            <input
                                                type="number"
                                                value={item.return_quantity || ''}
                                                onChange={(e) => onQuantityChange(index, e.target.value)}
                                                step="0.001"
                                                min="0.001"
                                                max={availableQty}
                                                placeholder="0.000"
                                                disabled={disabled}
                                            />
                                            <small className="stock-hint">
                                                {translations?.available || 'Available'}: {availableQty.toFixed(2)} {item.unit}
                                            </small>
                                        </div>

                                        <div className={`mobile-field half ${errors[`rolls_${index}`] ? 'has-error' : ''}`}>
                                            <label>{translations?.rollsToReturn || 'Rolls to Return'}</label>
                                            <input
                                                type="number"
                                                value={item.return_rolls || ''}
                                                onChange={(e) => onRollsChange(index, e.target.value)}
                                                step="1"
                                                min="0"
                                                max={availableRolls}
                                                placeholder="0"
                                                disabled={disabled}
                                            />
                                            <small className="stock-hint">
                                                {translations?.availableRolls || 'Available Rolls'}: {availableRolls}
                                            </small>
                                        </div>
                                    </div>

                                    <div className="mobile-row">
                                        <div className="mobile-field half">
                                            <label>{translations?.price || 'Price'}</label>
                                            <input type="number" value={item.price} readOnly className="readonly-input" />
                                        </div>
                                        <div className="mobile-field half">
                                            <label>{translations?.total || 'Total'}</label>
                                            <input
                                                type="text"
                                                value={calculateTotal(item.price, item.return_quantity)}
                                                readOnly
                                                disabled
                                                className="readonly-input total-display"
                                            />
                                        </div>
                                    </div>

                                    {errors[`quantity_${index}`] && (
                                        <div className="error-text"><FontAwesomeIcon icon={faExclamationTriangle} />{errors[`quantity_${index}`]}</div>
                                    )}
                                    {errors[`rolls_${index}`] && (
                                        <div className="error-text"><FontAwesomeIcon icon={faExclamationTriangle} />{errors[`rolls_${index}`]}</div>
                                    )}
                                </div>
                            </div>
                        );
                    })}
                </div>
            </div>
        );
    }

    return (
        <div className="items-table-container">
            <div className="table-header-actions">
                <h4>
                    <FontAwesomeIcon icon={faBoxes} className="header-icon-only" />
                    {translations?.itemsToReturn || 'Items to Return'}
                </h4>
            </div>
            <div className="table-wrapper">
                <table className="items-table fab-return-items-table">
                    <thead>
                        <tr>
                            <th className="product-col">{translations?.productName || 'Product'}</th>
                            <th className="color-col">{translations?.colorCode || 'Color'}</th>
                            <th className="batch-col">{translations?.batchNumber || 'Batch'}</th>
                            <th className="unit-col">{translations?.unit || 'Unit'}</th>
                            <th className="price-col">{translations?.price || 'Price'}</th>
                            <th className="qty-col">{translations?.quantityToReturn || 'Qty to Return'}</th>
                            <th className="rolls-col">{translations?.rollsToReturn || 'Rolls'}</th>
                            <th className="total-col">{translations?.total || 'Total'}</th>
                            <th className="action-col"></th>
                        </tr>
                    </thead>
                    <tbody>
                        {items.map((item, index) => {
                            const availableQty = getSafeAvailable(item);
                            const availableRolls = getSafeAvailableRolls(item);
                            return (
                                <tr key={index} className={errors[`quantity_${index}`] || errors[`rolls_${index}`] ? 'error-row' : ''}>
                                    <td className="product-cell">
                                        <input type="text" value={item.product_name} readOnly className="readonly-input" />
                                        {item.barcode && (
                                            <small className="barcode-display">
                                                <FontAwesomeIcon icon={faBarcode} size="xs" /> {item.barcode}
                                            </small>
                                        )}
                                    </td>
                                    <td className="color-cell">
                                        <input type="text" value={item.product_colorcode || ''} readOnly className="readonly-input" />
                                    </td>
                                    <td className="batch-cell">
                                        <input type="text" value={item.batch_no || ''} readOnly className="readonly-input" />
                                    </td>
                                    <td className="unit-cell">
                                        <input type="text" value={item.unit} readOnly className="readonly-input" />
                                    </td>
                                    <td className="price-cell">
                                        <input type="number" value={item.price} readOnly className="readonly-input" />
                                    </td>
                                    <td className="qty-cell">
                                        <input
                                            type="number"
                                            value={item.return_quantity || ''}
                                            onChange={(e) => onQuantityChange(index, e.target.value)}
                                            step="0.001"
                                            min="0.001"
                                            max={availableQty}
                                            placeholder="0.000"
                                            disabled={disabled}
                                            className={errors[`quantity_${index}`] ? 'error' : ''}
                                        />
                                        <small className="stock-hint">
                                            {translations?.available || 'Available'}: {availableQty.toFixed(2)} {item.unit}
                                        </small>
                                        {errors[`quantity_${index}`] && (
                                            <div className="table-error"><FontAwesomeIcon icon={faExclamationTriangle} />{errors[`quantity_${index}`]}</div>
                                        )}
                                    </td>
                                    <td className="rolls-cell">
                                        <input
                                            type="number"
                                            value={item.return_rolls || ''}
                                            onChange={(e) => onRollsChange(index, e.target.value)}
                                            step="1"
                                            min="0"
                                            max={availableRolls}
                                            placeholder="0"
                                            disabled={disabled}
                                            className={errors[`rolls_${index}`] ? 'error' : ''}
                                        />
                                        <small className="stock-hint">
                                            {translations?.availableRolls || 'Available Rolls'}: {availableRolls}
                                        </small>
                                        {errors[`rolls_${index}`] && (
                                            <div className="table-error"><FontAwesomeIcon icon={faExclamationTriangle} />{errors[`rolls_${index}`]}</div>
                                        )}
                                    </td>
                                    <td className="total-cell">
                                        <input
                                            type="text"
                                            value={calculateTotal(item.price, item.return_quantity)}
                                            readOnly
                                            disabled
                                            className="readonly-input total-display"
                                        />
                                    </td>
                                    <td className="action-cell">
                                        <button
                                            type="button"
                                            className="table-remove-btn"
                                            onClick={() => onRemove(index)}
                                            disabled={disabled}
                                            title={translations?.remove || 'Remove'}
                                        >
                                            <FontAwesomeIcon icon={faTrash} />
                                        </button>
                                    </td>
                                </tr>
                            );
                        })}
                    </tbody>
                </table>
            </div>
        </div>
    );
};

// ==============================================
// Fab Return Receipt Modal
// ==============================================
const FabReturnReceiptModal = ({ show, returnData, businessInfo, shopInfo, translations, onPrint, onClose }) => {
    const receiptRef = useRef(null);

    const formatDate = (dateString) => {
        if (!dateString) return '';
        const date = new Date(dateString);
        if (isNaN(date.getTime())) {
            return new Date().toLocaleDateString('en-IN', { day: '2-digit', month: '2-digit', year: 'numeric' });
        }
        return date.toLocaleDateString('en-IN', { day: '2-digit', month: '2-digit', year: 'numeric' });
    };

    const formatTime = () => {
        return new Date().toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: true });
    };

    const calculateTotals = () => {
        if (!returnData?.items) return { totalQuantity: 0, totalRolls: 0 };
        
        let totalQuantity = 0;
        let totalRolls = 0;
        
        returnData.items.forEach(item => {
            totalQuantity += parseFloat(item.quantity) || 0;
            totalRolls += parseInt(item.rolls) || 0;
        });
        
        return { totalQuantity: totalQuantity.toFixed(2), totalRolls };
    };

    const getProductDisplayName = (item) => {
        let name = item.product_name || '';
        if (item.product_colorcode) {
            name += ` - ${item.product_colorcode}`;
        }
        return name;
    };

    const handlePrint = () => {
        if (receiptRef.current) {
            const printContent = receiptRef.current.innerHTML;
            const printWindow = window.open('', '_blank');
            if (printWindow) {
                printWindow.document.write('<!DOCTYPE html><html><head><title>Fabric Return Receipt</title><meta charset="UTF-8">');
                printWindow.document.write(`
                    <style>
                        @page { size: A5; margin: 8mm 6mm; }
                        body { font-family: 'Inter', sans-serif; background: white; margin: 0; padding: 0; }
                        .receipt-a5 { max-width: 148mm; margin: 0 auto; padding: 5mm; }
                        .header-section { text-align: center; border-bottom: 2px solid #1e293b; padding-bottom: 3mm; margin-bottom: 3mm; }
                        .business-name { font-size: 14pt; font-weight: 700; color: #0f172a; }
                        .business-address { font-size: 7pt; color: #475569; }
                        .receipt-title { font-size: 10pt; font-weight: 700; text-transform: uppercase; color: #dc2626; text-align: center; margin: 2mm 0; }
                        .info-grid { display: flex; flex-wrap: wrap; justify-content: space-between; margin: 2mm 0; border-bottom: 1px dashed #cbd5e1; padding-bottom: 2mm; }
                        .info-item { font-size: 7.5pt; }
                        .info-label { font-weight: 600; color: #475569; }
                        .customer-section { border: 1px solid #e2e8f0; padding: 2mm; margin: 2mm 0; border-radius: 4px; background: #f8fafc; font-size: 7.5pt; }
                        .items-table { width: 100%; border-collapse: collapse; margin: 2mm 0; font-size: 7pt; }
                        .items-table th { background: #1e293b; color: white; padding: 2mm 1mm; text-align: center; }
                        .items-table td { padding: 1.5mm 1mm; border: 1px solid #cbd5e1; text-align: center; }
                        .product-name { font-weight: 700; }
                        .totals-section { margin: 3mm 0; border-top: 2px solid #1e293b; padding-top: 2mm; }
                        .total-row { display: flex; justify-content: space-between; margin: 1mm 0; font-size: 8pt; }
                        .refund-row { font-size: 10pt; font-weight: 700; color: #dc2626; border-top: 2px solid #dc2626; border-bottom: 2px solid #dc2626; padding: 1.5mm 0; margin: 2mm 0; }
                        .footer-section { text-align: center; margin-top: 3mm; padding-top: 2mm; border-top: 1px dashed #cbd5e1; font-size: 6.5pt; }
                        .thankyou-text { font-size: 8pt; font-weight: 600; }
                    </style>
                `);
                printWindow.document.write('</head><body><div class="receipt-a5">');
                printWindow.document.write(printContent);
                printWindow.document.write('</div></body></html>');
                printWindow.document.close();
                printWindow.onload = () => { printWindow.print(); printWindow.onafterprint = () => printWindow.close(); };
            }
        }
        if (onPrint) onPrint();
    };

    if (!show || !returnData) return null;

    const shopName = shopInfo?.name || businessInfo?.businessname || 'Fabric Shop';
    const shopAddress = businessInfo?.businessaddress || '';
    const shopPhone = businessInfo?.phonenumber || '';
    const { totalQuantity, totalRolls } = calculateTotals();

    return (
        <div className="modal-overlay-modern" onClick={onClose}>
            <div className="modal-content-modern receipt-modal" onClick={e => e.stopPropagation()} style={{ maxWidth: '550px' }}>
                <div className="modal-header-modern">
                    <h3><FontAwesomeIcon icon={faReceipt} />{translations?.returnReceipt || 'Fabric Return Receipt'}</h3>
                    <button className="modal-close" onClick={onClose}><FontAwesomeIcon icon={faTimes} /></button>
                </div>

                <div className="modal-body-modern" style={{ maxHeight: '70vh', overflowY: 'auto' }}>
                    <div ref={receiptRef} className="receipt-content-a5">
                        <div className="header-section">
                            <div className="business-name">{shopName}</div>
                            {shopAddress && <div className="business-address">{shopAddress}</div>}
                            {shopPhone && <div className="business-address">📞 {shopPhone}</div>}
                        </div>

                        <div className="receipt-title">{translations?.returnReceipt || 'FABRIC RETURN RECEIPT'}</div>

                        <div className="info-grid">
                            <div className="info-item"><span className="info-label">{translations?.returnNumber || 'Return #'}:</span> {returnData?.return_number || 'N/A'}</div>
                            <div className="info-item"><span className="info-label">{translations?.date || 'Date'}:</span> {formatDate(returnData?.return_date)}</div>
                            <div className="info-item"><span className="info-label">{translations?.time || 'Time'}:</span> {formatTime()}</div>
                            <div className="info-item"><span className="info-label">{translations?.cashier || 'Cashier'}:</span> {returnData?.username || 'Staff'}</div>
                            <div className="info-item"><span className="info-label">{translations?.originalBillNumber || 'Original Invoice'}:</span> {returnData?.original_bill_number || 'N/A'}</div>
                        </div>

                        {(returnData?.customer_name || returnData?.customer_phone) && (
                            <div className="customer-section">
                                {returnData?.customer_name && <div><strong>{returnData.customer_name}</strong></div>}
                                {returnData?.customer_phone && <div>📞 {returnData.customer_phone}</div>}
                            </div>
                        )}

                        <table className="items-table">
                            <thead>
                                <tr>
                                    <th>{translations?.productName || 'Product'}</th>
                                    <th>{translations?.quantityToReturn || 'Qty'}</th>
                                    <th>{translations?.rollsToReturn || 'Rolls'}</th>
                                    <th>{translations?.price || 'Price'}</th>
                                    <th>{translations?.total || 'Total'}</th>
                                </tr>
                            </thead>
                            <tbody>
                                {returnData?.items?.map((item, idx) => {
                                    const itemQty = parseFloat(item.quantity) || 0;
                                    const itemRolls = parseInt(item.rolls) || 0;
                                    const itemPrice = parseFloat(item.price_per_unit) || 0;
                                    const itemTotal = itemQty * itemPrice;
                                    return (
                                        <tr key={idx}>
                                            <td className="product-name">{getProductDisplayName(item)}</td>
                                            <td>{itemQty.toFixed(2)} {item.unit}</td>
                                            <td>{itemRolls}</td>
                                            <td>{itemPrice.toFixed(2)}</td>
                                            <td>{itemTotal.toFixed(2)}</td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                            <tfoot>
                                <tr style={{ background: '#f1f5f9' }}>
                                    <td colSpan="2" className="text-right" style={{ fontWeight: 'bold' }}>Total: {totalQuantity}</td>
                                    <td style={{ fontWeight: 'bold' }}>{totalRolls}</td>
                                    <td colSpan="2" style={{ fontWeight: 'bold' }}>{parseFloat(returnData?.grand_total || 0).toFixed(2)}</td>
                                </tr>
                            </tfoot>
                        </table>

                        <div className="totals-section">
                            <div className="total-row"><span>{translations?.subtotal || 'Subtotal'}:</span><span>{parseFloat(returnData?.subtotal || 0).toFixed(2)}</span></div>
                            {parseFloat(returnData?.discount_amount || 0) > 0 && (
                                <div className="total-row"><span>{translations?.discountAmount || 'Discount'}:</span><span>-{parseFloat(returnData?.discount_amount || 0).toFixed(2)}</span></div>
                            )}
                            {parseFloat(returnData?.tax_amount || 0) > 0 && (
                                <div className="total-row"><span>{translations?.taxAmount || 'Tax'}:</span><span>+{parseFloat(returnData?.tax_amount || 0).toFixed(2)}</span></div>
                            )}
                        </div>

                        <div className="refund-row">
                            <span><strong>{translations?.refundAmount || 'REFUND AMOUNT'}:</strong></span>
                            <span><strong>{parseFloat(returnData?.refund_amount || 0).toFixed(2)}</strong></span>
                        </div>

                        <div className="total-row"><span>{translations?.refundMethod || 'Refund Method'}:</span><span>{returnData?.payment_method?.toUpperCase() || 'CASH'}</span></div>

                        {returnData?.return_reason && (
                            <div style={{ margin: '2mm 0', padding: '1.5mm', background: '#fef2f2', borderRadius: '4px', fontSize: '6.5pt' }}>
                                <strong>{translations?.returnReason || 'Return Reason'}:</strong> {returnData.return_reason}
                            </div>
                        )}

                        <div className="footer-section">
                            <div className="thankyou-text">{translations?.thankYou || 'Thank you for your business!'}</div>
                            <div>{translations?.poweredBy || 'Powered by OurZest'}</div>
                        </div>
                    </div>
                </div>

                <div className="modal-footer-modern">
                    <button className="btn-modern btn-secondary-modern" onClick={onClose}><FontAwesomeIcon icon={faTimes} />{translations?.close || 'Close'}</button>
                    <button className="btn-modern btn-primary-modern" onClick={handlePrint} style={{ background: 'linear-gradient(135deg, #dc2626, #991b1b)' }}>
                        <FontAwesomeIcon icon={faPrint} /> {translations?.print || 'Print Receipt'}
                    </button>
                </div>
            </div>
        </div>
    );
};

// ==============================================
// Business Info Component
// ==============================================
const BusinessInfo = ({ businessInfo, translations }) => {
    if (!businessInfo) return null;
    return (
        <div className="business-info-card" style={{ background: 'linear-gradient(135deg, #f8fafc, #f1f5f9)', borderRadius: '12px', display: 'flex', alignItems: 'center', gap: '15px', padding: '15px', marginBottom: '20px' }}>
            {businessInfo.bussinesslogo && <img src={businessInfo.bussinesslogo} alt="Logo" style={{ width: '50px', height: '50px', objectFit: 'contain', borderRadius: '8px' }} />}
            <div className="business-details">
                <div style={{ fontSize: '16px', fontWeight: '700', color: '#0f172a' }}>{businessInfo.businessname}</div>
                {businessInfo.businessaddress && <div style={{ fontSize: '12px', color: '#64748b' }}><FontAwesomeIcon icon={faMapMarkerAlt} size="xs" /> {businessInfo.businessaddress}</div>}
                {businessInfo.phonenumber && <div style={{ fontSize: '12px', color: '#64748b' }}><FontAwesomeIcon icon={faPhone} size="xs" /> {businessInfo.phonenumber}</div>}
            </div>
        </div>
    );
};

// ==============================================
// Main FabReturn Component
// ==============================================
const FabReturn = () => {
    const { user, currentShop } = useAuth();

    const [shopId, setShopId] = useState(() => {
        if (user?.shop_id) return user.shop_id;
        return localStorage.getItem('current_shop_id') || '';
    });

    const [shopInfo, setShopInfo] = useState({
        id: shopId,
        name: currentShop?.shop_name || localStorage.getItem('current_shop_name') || ''
    });

    const [businessInfo, setBusinessInfo] = useState(null);

    const [searchTerm, setSearchTerm] = useState('');
    const [searching, setSearching] = useState(false);
    const [invoiceFound, setInvoiceFound] = useState(false);
    const [originalSale, setOriginalSale] = useState(null);

    const [formData, setFormData] = useState({
        return_number: '',
        return_number_int: 0,
        sell_id: null,
        date: new Date().toISOString().split('T')[0],
        customer_id: null,
        customer_name: '',
        customer_phone: '',
        customer_gst: '',
        subtotal: '0.00',
        discount_percent: '0.00',
        discount_amount: '0.00',
        tax_percent: '0.00',
        tax_amount: '0.00',
        grand_total: '0.00',
        refund_amount: '0.00',
        payment_method: 'cash',
        return_reason: '',
        notes: '',
        items: [],
        themeColor: localStorage.getItem('ourzest_theme') || '#8b5cf6'
    });

    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);
    const [errors, setErrors] = useState({});
    const [currentLanguage, setCurrentLanguage] = useState(() => localStorage.getItem('ourzest_language') || 'en');
    const [translations, setTranslations] = useState({});
    const [isTranslating, setIsTranslating] = useState(false);

    const [showReceiptModal, setShowReceiptModal] = useState(false);
    const [currentReturn, setCurrentReturn] = useState(null);

    const [toast, setToast] = useState({ show: false, type: '', message: '', direction: 'ltr', progress: 100, timeLeft: 3 });

    const toastTimerRef = useRef(null);
    const isSubmittingRef = useRef(false);

    const loadTranslations = useCallback(async (lang) => {
        if (!lang) return;
        setIsTranslating(true);
        try {
            const trans = await fabReturnTranslator.getTranslations(lang);
            setTranslations(trans);
            const isRTL = lang === 'ur' || lang === 'ar';
            document.documentElement.dir = isRTL ? 'rtl' : 'ltr';
            document.body.dir = isRTL ? 'rtl' : 'ltr';
        } catch (error) {
            console.error('Error loading translations:', error);
        } finally {
            setIsTranslating(false);
        }
    }, []);

    useEffect(() => {
        loadTranslations(currentLanguage);
        const handleLanguageChange = () => {
            const newLang = localStorage.getItem('ourzest_language') || 'en';
            if (newLang !== currentLanguage) {
                setCurrentLanguage(newLang);
                loadTranslations(newLang);
            }
        };
        window.addEventListener('storage', handleLanguageChange);
        const intervalId = setInterval(() => {
            const storedLang = localStorage.getItem('ourzest_language') || 'en';
            if (storedLang !== currentLanguage) {
                setCurrentLanguage(storedLang);
                loadTranslations(storedLang);
            }
        }, 500);
        const translationListener = (lang) => {
            if (lang !== currentLanguage) {
                setCurrentLanguage(lang);
                loadTranslations(lang);
            }
        };
        fabReturnTranslator.addListener(translationListener);
        return () => {
            window.removeEventListener('storage', handleLanguageChange);
            clearInterval(intervalId);
            fabReturnTranslator.removeListener(translationListener);
        };
    }, [currentLanguage, loadTranslations]);

    const showToast = useCallback((type, message) => {
        if (toastTimerRef.current) clearInterval(toastTimerRef.current);
        const isRTL = currentLanguage === 'ur' || currentLanguage === 'ar';
        setToast({ show: true, type, message, direction: isRTL ? 'rtl' : 'ltr', progress: 100, timeLeft: 3 });
        let timeLeft = 3;
        const timer = setInterval(() => {
            timeLeft -= 0.1;
            setToast(prev => ({ ...prev, progress: (timeLeft / 3) * 100, timeLeft: parseFloat(timeLeft.toFixed(1)) }));
            if (timeLeft <= 0) { clearInterval(timer); setToast(prev => ({ ...prev, show: false })); }
        }, 100);
        toastTimerRef.current = timer;
    }, [currentLanguage]);

    useEffect(() => {
        return () => { if (toastTimerRef.current) clearInterval(toastTimerRef.current); isSubmittingRef.current = false; };
    }, []);

    const loadBusinessInfo = useCallback(async () => {
        if (!user?.username) return;
        try {
            const response = await fetch('https://ourzestapp.online/oz_backend/fab_sale.php', {
                method: 'POST', headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ action: 'get_business_info', username: user.username })
            });
            const data = await response.json();
            if (data.status === 'success') setBusinessInfo(data.data);
        } catch (error) { console.error('Error loading business info:', error); }
    }, [user?.username]);

    useEffect(() => {
        const applyTheme = (color) => {
            try {
                if (!/^#[0-9A-F]{6}$/i.test(color)) color = '#8b5cf6';
                const hex = color.replace('#', '');
                const r = parseInt(hex.substring(0, 2), 16);
                const g = parseInt(hex.substring(2, 4), 16);
                const b = parseInt(hex.substring(4, 6), 16);
                const adjust = (amount) => {
                    let newR = Math.max(0, Math.min(255, r + amount));
                    let newG = Math.max(0, Math.min(255, g + amount));
                    let newB = Math.max(0, Math.min(255, b + amount));
                    return `#${newR.toString(16).padStart(2, '0')}${newG.toString(16).padStart(2, '0')}${newB.toString(16).padStart(2, '0')}`;
                };
                const palette = {
                    'purchase-primary-color': color,
                    'purchase-primary-dark': adjust(-40),
                    'purchase-primary-light': adjust(40),
                    'purchase-primary-ultralight': adjust(20),
                    'purchase-primary-gradient': `linear-gradient(135deg, ${color}, ${adjust(-20)})`,
                    'purchase-primary-shadow': `${color}20`
                };
                Object.entries(palette).forEach(([key, value]) => document.documentElement.style.setProperty(`--${key}`, value));
            } catch (err) { console.error("Error applying theme:", err); }
        };
        applyTheme(formData.themeColor);
        const handleThemeChange = () => {
            const theme = localStorage.getItem('ourzest_theme') || '#8b5cf6';
            if (theme !== formData.themeColor) { setFormData(prev => ({ ...prev, themeColor: theme })); applyTheme(theme); }
        };
        handleThemeChange();
        window.addEventListener('storage', handleThemeChange);
        const interval = setInterval(handleThemeChange, 1000);
        return () => { window.removeEventListener('storage', handleThemeChange); clearInterval(interval); };
    }, [formData.themeColor]);

    useEffect(() => {
        if (currentShop) setShopInfo({ id: currentShop.shop_id, name: currentShop.shop_name });
    }, [currentShop]);

    useEffect(() => {
        if (shopId) { getNextReturnNumber(); }
        loadBusinessInfo();
    }, [shopId, loadBusinessInfo]);

    useEffect(() => { calculateTotals(); }, [formData.items, formData.discount_percent, formData.tax_percent]);

    const getNextReturnNumber = async () => {
        if (!shopId) return;
        try {
            const response = await fetch('https://ourzestapp.online/oz_backend/fab_return.php', {
                method: 'POST', headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ action: 'get_next_return_number', shop_id: shopId })
            });
            const data = await response.json();
            if (data.status === 'success') {
                setFormData(prev => ({ ...prev, return_number: data.data.return_number, return_number_int: data.data.return_number_int }));
            }
        } catch (error) { console.error('Error getting next return number:', error); }
    };

    const searchInvoice = async () => {
        if (!searchTerm.trim()) {
            showToast('error', translations?.searchPlaceholder || 'Please enter invoice number');
            return;
        }
        if (!shopId) {
            showToast('error', translations?.noShopSelected || 'No shop selected');
            return;
        }
        setSearching(true);
        setInvoiceFound(false);
        setOriginalSale(null);
        setFormData(prev => ({
            ...prev, sell_id: null, customer_id: null, customer_name: '', customer_phone: '', customer_gst: '',
            subtotal: '0.00', discount_percent: '0.00', discount_amount: '0.00',
            tax_percent: '0.00', tax_amount: '0.00', grand_total: '0.00', refund_amount: '0.00', items: []
        }));

        try {
            const response = await fetch('https://ourzestapp.online/oz_backend/fab_return.php', {
                method: 'POST', headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ action: 'search_invoice', search_term: searchTerm, shop_id: shopId })
            });
            const data = await response.json();
            if (data.status === 'success' && data.data) {
                setInvoiceFound(true);
                setOriginalSale(data.data);

                const returnItems = data.data.items.map(item => ({
                    ...item,
                    return_quantity: '',
                    return_rolls: '',
                    sell_item_id: item.item_id || item.sell_item_id,
                    available_for_return: parseFloat(item.available_for_return) || parseFloat(item.quantity) || 0,
                    available_rolls: parseFloat(item.available_rolls) || 0,
                    price: parseFloat(item.price) || parseFloat(item.price_per_unit) || 0,
                    original_quantity: parseFloat(item.original_quantity) || parseFloat(item.quantity) || 0
                }));

                setFormData(prev => ({
                    ...prev, sell_id: data.data.id, customer_id: data.data.customer_id,
                    customer_name: data.data.customer_name || (data.data.customer_details_name || ''),
                    customer_phone: data.data.customer_phone || (data.data.customer_details_phone || ''),
                    customer_gst: data.data.customer_gst || '',
                    items: returnItems
                }));
                showToast('success', `${translations?.invoiceFound || 'Invoice found'}: #${data.data.bill_number}`);
            } else {
                showToast('error', data.message || translations?.invoiceNotFound || 'Invoice not found or no items available for return');
            }
        } catch (error) {
            console.error('Error searching invoice:', error);
            showToast('error', translations?.networkError || 'Network error occurred');
        } finally {
            setSearching(false);
        }
    };

    const handleQuantityChange = (index, value) => {
        const updatedItems = [...formData.items];
        const item = updatedItems[index];
        const qty = value === '' ? '' : parseFloat(value);
        if (qty !== '' && (isNaN(qty) || qty < 0)) return;
        const maxAvailable = item.available_for_return || 0;
        if (qty !== '' && qty > maxAvailable) {
            setErrors(prev => ({ ...prev, [`quantity_${index}`]: `${translations?.quantityExceedsAvailable || 'Cannot return more than'} ${maxAvailable.toFixed(2)} ${item.unit}` }));
            return;
        }
        if (errors[`quantity_${index}`]) {
            setErrors(prev => { const newErrors = { ...prev }; delete newErrors[`quantity_${index}`]; return newErrors; });
        }
        item.return_quantity = qty === '' ? '' : qty;
        setFormData(prev => ({ ...prev, items: updatedItems }));
    };

    const handleRollsChange = (index, value) => {
        const updatedItems = [...formData.items];
        const item = updatedItems[index];
        const rolls = value === '' ? '' : parseInt(value);
        if (rolls !== '' && (isNaN(rolls) || rolls < 0)) return;
        const maxAvailableRolls = item.available_rolls || 0;
        if (rolls !== '' && rolls > maxAvailableRolls) {
            setErrors(prev => ({ ...prev, [`rolls_${index}`]: `${translations?.rollsExceedsAvailable || 'Cannot return more than'} ${maxAvailableRolls} rolls` }));
            return;
        }
        if (errors[`rolls_${index}`]) {
            setErrors(prev => { const newErrors = { ...prev }; delete newErrors[`rolls_${index}`]; return newErrors; });
        }
        item.return_rolls = rolls === '' ? '' : rolls;
        setFormData(prev => ({ ...prev, items: updatedItems }));
    };

    const removeItem = (index) => {
        if (formData.items.length > 1) {
            const updatedItems = [...formData.items];
            updatedItems.splice(index, 1);
            setFormData(prev => ({ ...prev, items: updatedItems }));
            setErrors(prev => { const newErrors = { ...prev }; delete newErrors[`quantity_${index}`]; delete newErrors[`rolls_${index}`]; return newErrors; });
        }
    };

    const calculateTotals = () => {
        let subtotal = 0;
        let hasAnyReturn = false;
        formData.items.forEach(item => {
            if (item.return_quantity && parseFloat(item.return_quantity) > 0) {
                hasAnyReturn = true;
                const price = parseFloat(item.price) || 0;
                const quantity = parseFloat(item.return_quantity) || 0;
                subtotal += price * quantity;
            }
        });
        const discountPercent = parseFloat(formData.discount_percent) || 0;
        const discountAmount = (subtotal * discountPercent) / 100;
        const taxPercent = parseFloat(formData.tax_percent) || 0;
        const taxAmount = (subtotal * taxPercent) / 100;
        const grandTotal = subtotal - discountAmount + taxAmount;
        const refundAmount = grandTotal;
        setFormData(prev => ({
            ...prev, subtotal: subtotal.toFixed(2), discount_amount: discountAmount.toFixed(2),
            tax_amount: taxAmount.toFixed(2), grand_total: grandTotal.toFixed(2), refund_amount: refundAmount.toFixed(2)
        }));
        return hasAnyReturn;
    };

    const validateForm = () => {
        const newErrors = {};
        if (!formData.sell_id) newErrors.invoice = translations?.invoiceNotFound || 'Please search and select an invoice first';
        if (!formData.items || formData.items.length === 0) {
            newErrors.items = translations?.itemsRequired || 'At least one item is required for return';
        } else {
            let hasValidReturn = false;
            formData.items.forEach((item, index) => {
                const qty = parseFloat(item.return_quantity) || 0;
                if (qty > 0) {
                    hasValidReturn = true;
                    const maxAvailable = item.available_for_return || 0;
                    if (qty > maxAvailable) newErrors[`quantity_${index}`] = `${translations?.quantityExceedsAvailable || 'Cannot return more than'} ${maxAvailable.toFixed(2)} ${item.unit}`;
                    if (qty <= 0) newErrors[`quantity_${index}`] = translations?.invalidQuantity || 'Please enter a valid quantity';
                }
                const rolls = parseInt(item.return_rolls) || 0;
                if (rolls > 0) {
                    const maxAvailableRolls = item.available_rolls || 0;
                    if (rolls > maxAvailableRolls) newErrors[`rolls_${index}`] = `${translations?.rollsExceedsAvailable || 'Cannot return more than'} ${maxAvailableRolls} rolls`;
                }
            });
            if (!hasValidReturn) newErrors.items = translations?.itemsRequired || 'Please enter at least one item to return';
        }
        if (!formData.return_reason.trim()) newErrors.return_reason = translations?.returnReasonRequired || 'Return reason is required';
        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
        if (name === 'discount_percent' || name === 'tax_percent') calculateTotals();
        if (errors[name]) setErrors(prev => ({ ...prev, [name]: '' }));
    };

    const handlePrintFromModal = () => setShowReceiptModal(false);

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (isSubmittingRef.current) { showToast('warning', 'Please wait, processing your request...'); return; }
        if (!shopId) { showToast('error', translations?.noShopSelected || 'No shop selected.'); return; }
        if (!validateForm()) { const firstError = document.querySelector('.has-error, .error-message-modern'); if (firstError) firstError.scrollIntoView({ behavior: 'smooth', block: 'center' }); return; }
        isSubmittingRef.current = true;
        setSaving(true);
        try {
            const currentUsername = user?.username || '';
            if (!currentUsername) throw new Error("User not authenticated");
            const returnItems = formData.items.filter(item => parseFloat(item.return_quantity) > 0 || parseInt(item.return_rolls) > 0).map(item => ({
                sell_item_id: item.sell_item_id || item.item_id,
                product_id: item.product_id,
                product_name: item.product_name,
                product_colorcode: item.product_colorcode,
                batch_no: item.batch_no,
                unit: item.unit,
                quantity: parseFloat(item.return_quantity) || 0,
                rolls: parseInt(item.return_rolls) || 0,
                price_per_unit: parseFloat(item.price),
                total: parseFloat(item.price) * (parseFloat(item.return_quantity) || 0)
            }));
            const payload = {
                action: 'add', username: currentUsername, shop_id: shopId, sell_id: formData.sell_id,
                date: formData.date, customer_id: formData.customer_id, customer_name: formData.customer_name,
                customer_phone: formData.customer_phone, customer_gst: formData.customer_gst,
                subtotal: parseFloat(formData.subtotal), discount_percent: parseFloat(formData.discount_percent),
                discount_amount: parseFloat(formData.discount_amount), tax_percent: parseFloat(formData.tax_percent),
                tax_amount: parseFloat(formData.tax_amount), grand_total: parseFloat(formData.grand_total),
                refund_amount: parseFloat(formData.refund_amount), payment_method: formData.payment_method,
                return_reason: formData.return_reason, notes: formData.notes, items: returnItems
            };
            const response = await fetch('https://ourzestapp.online/oz_backend/fab_return.php', {
                method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(payload)
            });
            const data = await response.json();
            if (data.status === 'success') {
                showToast('success', translations?.returnProcessed || 'Fabric return processed successfully!');
                const returnForReceipt = {
                    ...data.data, items: returnItems, subtotal: formData.subtotal, discount_amount: formData.discount_amount,
                    tax_amount: formData.tax_amount, grand_total: formData.grand_total, refund_amount: formData.refund_amount,
                    payment_method: formData.payment_method, return_reason: formData.return_reason, notes: formData.notes,
                    customer_name: formData.customer_name, customer_phone: formData.customer_phone,
                    customer_gst: formData.customer_gst, username: currentUsername,
                    original_bill_number: originalSale?.bill_number, return_date: formData.date
                };
                setCurrentReturn(returnForReceipt);
                setFormData(prev => ({
                    ...prev, date: new Date().toISOString().split('T')[0], customer_id: null, customer_name: '',
                    customer_phone: '', customer_gst: '', subtotal: '0.00', discount_percent: '0.00',
                    discount_amount: '0.00', tax_percent: '0.00', tax_amount: '0.00', grand_total: '0.00',
                    refund_amount: '0.00', payment_method: 'cash', return_reason: '', notes: '', items: []
                }));
                setSearchTerm(''); setInvoiceFound(false); setOriginalSale(null); setErrors({});
                getNextReturnNumber();
                setTimeout(() => setShowReceiptModal(true), 500);
            } else {
                if (data.duplicate_prevention) showToast('warning', data.message || 'Please wait a moment before submitting again.');
                else showToast('error', data.message || translations?.failedProcess || 'Failed to process return');
            }
        } catch (error) {
            console.error('Error processing return:', error);
            showToast('error', error.message || translations?.networkError || 'Network error occurred');
        } finally {
            setTimeout(() => { isSubmittingRef.current = false; setSaving(false); }, 2000);
        }
    };

    const handleClear = () => {
        setFormData(prev => ({
            ...prev, date: new Date().toISOString().split('T')[0], customer_id: null, customer_name: '',
            customer_phone: '', customer_gst: '', subtotal: '0.00', discount_percent: '0.00',
            discount_amount: '0.00', tax_percent: '0.00', tax_amount: '0.00', grand_total: '0.00',
            refund_amount: '0.00', payment_method: 'cash', return_reason: '', notes: '', items: []
        }));
        setSearchTerm(''); setInvoiceFound(false); setOriginalSale(null); setErrors({});
        getNextReturnNumber();
    };

    const isRTL = currentLanguage === 'ur' || currentLanguage === 'ar';

    return (
        <div className={`purchases-form-independent ${isRTL ? 'rtl' : ''} ${isTranslating ? 'translating' : ''}`}>
            <ToastNotification toast={toast} setToast={setToast} translations={translations} />
            <FabReturnReceiptModal show={showReceiptModal} returnData={currentReturn} businessInfo={businessInfo} shopInfo={shopInfo} translations={translations} onPrint={handlePrintFromModal} onClose={() => setShowReceiptModal(false)} />
            
            <div className="form-header-modern">
                <div className="header-content">
                    <div className="header-icon"><FontAwesomeIcon icon={faUndo} /></div>
                    <div className="header-text">
                        <h1>{translations?.processFabricReturn || 'Process Fabric Return'}</h1>
                        <p>{translations?.returnFabricItems || 'Search invoice and return fabric items'}</p>
                    </div>
                </div>
                {isTranslating && <div className="translation-indicator"><FontAwesomeIcon icon={faSpinner} spin /><span>Translating...</span></div>}
            </div>

            <BusinessInfo businessInfo={businessInfo} translations={translations} />

            <form onSubmit={handleSubmit} className="modern-form">
                <div className="form-section">
                    <div className="section-header"><h3 className="section-title"><FontAwesomeIcon icon={faSearch} /></h3></div>
                    <div className="form-grid-modern">
                        <div className="form-group-modern search-group">
                            <div className="input-container">
                                <FontAwesomeIcon icon={faSearch} className="input-icon search-icon" />
                                <input type="text" id="invoice_search" value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} onKeyPress={(e) => e.key === 'Enter' && searchInvoice()} placeholder=" " disabled={saving} dir={isRTL ? 'rtl' : 'ltr'} autoComplete="off" />
                                <label htmlFor="invoice_search" className="input-label">{translations?.searchPlaceholder || 'Enter Invoice'}</label>
                                <button type="button" className="search-btn generate-btn" onClick={searchInvoice} disabled={searching || saving}>
                                    {searching ? <FontAwesomeIcon icon={faSpinner} spin /> : <FontAwesomeIcon icon={faSearch} />}
                                </button>
                            </div>
                            {errors.invoice && <div className="error-message-modern"><FontAwesomeIcon icon={faExclamationTriangle} />{errors.invoice}</div>}
                        </div>
                    </div>
                </div>

                {invoiceFound && originalSale && (
                    <>
                        <div className="form-section">
                            <div className="section-header"><h3 className="section-title"><FontAwesomeIcon icon={faFileInvoice} />{translations?.returnInformation || 'Return Information'}</h3></div>
                            <div className="form-grid-modern">
                                <div className="form-group-modern"><div className="input-container"><FontAwesomeIcon icon={faFileInvoice} className="input-icon" /><input type="text" value={formData.return_number} readOnly className="readonly-input" /><label className="input-label">{translations?.returnNumber || 'Return Number'}</label></div></div>
                                <div className="form-group-modern"><div className="input-container"><FontAwesomeIcon icon={faCalendarAlt} className="input-icon" /><input type="date" name="date" value={formData.date} onChange={handleChange} disabled={saving} /><label className="input-label">{translations?.returnDate || 'Return Date'}</label></div></div>
                            </div>
                        </div>

                        <div className="form-section">
                            <div className="section-header"><h3 className="section-title"><FontAwesomeIcon icon={faHistory} />{translations?.originalInvoice || 'Original Invoice'}</h3></div>
                            <div className="form-grid-modern">
                                <div className="form-group-modern"><div className="input-container"><FontAwesomeIcon icon={faFileInvoice} className="input-icon" /><input type="text" value={originalSale.bill_number} readOnly className="readonly-input" /><label className="input-label">{translations?.originalBillNumber || 'Original Bill Number'}</label></div></div>
                                <div className="form-group-modern"><div className="input-container"><FontAwesomeIcon icon={faCalendarAlt} className="input-icon" /><input type="text" value={originalSale.sale_date} readOnly className="readonly-input" /><label className="input-label">{translations?.originalDate || 'Original Date'}</label></div></div>
                            </div>
                        </div>

                        <div className="form-section">
                            <div className="section-header"><h3 className="section-title"><FontAwesomeIcon icon={faUser} />{translations?.customerDetails || 'Customer Details'}</h3></div>
                            <div className="form-grid-modern">
                                <div className="form-group-modern"><div className="input-container"><FontAwesomeIcon icon={faUser} className="input-icon" /><input type="text" value={formData.customer_name} readOnly className="readonly-input" /><label className="input-label">{translations?.customerName || 'Customer Name'}</label></div></div>
                                <div className="form-group-modern"><div className="input-container"><FontAwesomeIcon icon={faPhone} className="input-icon" /><input type="text" value={formData.customer_phone} readOnly className="readonly-input" /><label className="input-label">{translations?.customerPhone || 'Phone'}</label></div></div>
                                <div className="form-group-modern"><div className="input-container"><FontAwesomeIcon icon={faTag} className="input-icon" /><input type="text" value={formData.customer_gst} readOnly className="readonly-input" /><label className="input-label">{translations?.customerGST || 'GST Number'}</label></div></div>
                            </div>
                        </div>

                        {formData.items.length > 0 && (
                            <div className="form-section">
                                <FabReturnItemsTable
                                    items={formData.items}
                                    onQuantityChange={handleQuantityChange}
                                    onRollsChange={handleRollsChange}
                                    onRemove={removeItem}
                                    errors={errors}
                                    disabled={saving}
                                    translations={translations}
                                    isRTL={isRTL}
                                />
                                {errors.items && <div className="error-message-modern"><FontAwesomeIcon icon={faExclamationTriangle} />{errors.items}</div>}
                            </div>
                        )}

                        {formData.items.some(item => parseFloat(item.return_quantity) > 0 || parseInt(item.return_rolls) > 0) && (
                            <>
                                <div className="form-section">
                                    <div className="form-group-modern">
                                        <div className="input-container textarea-container">
                                            <FontAwesomeIcon icon={faHistory} className="input-icon" />
                                            <input type="text" id="return_reason" name="return_reason" value={formData.return_reason} onChange={handleChange} placeholder=" " disabled={saving} className={errors.return_reason ? 'error' : ''} />
                                            <label htmlFor="return_reason" className="input-label">{translations?.returnReason || 'Return Reason'} *</label>
                                        </div>
                                        {errors.return_reason && <div className="error-message-modern"><FontAwesomeIcon icon={faExclamationTriangle} />{errors.return_reason}</div>}
                                    </div>
                                </div>

                                <div className="form-section">
                                    <div className="section-header"><h3 className="section-title"><FontAwesomeIcon icon={faCalculator} />{translations?.returnCalculations || 'Return Calculations'}</h3></div>
                                    <div className="calculations-grid fab-return-calculations">
                                        <div className="calculation-row grand-total"><span className="calculation-label">{translations?.grandTotal || 'Grand Total'}:</span><span className="calculation-value highlight">Rs. {formData.grand_total}</span></div>
                                        <div className="calculation-row"><span className="calculation-label">{translations?.refundMethod || 'Refund Method'}:</span>
                                            <div className="payment-method-selector" style={{ width: '150px' }}>
                                                <select value={formData.payment_method} onChange={(e) => setFormData(prev => ({ ...prev, payment_method: e.target.value }))} disabled={saving}>
                                                    <option value="cash">{translations?.cash || 'Cash'}</option>
                                                </select>
                                            </div>
                                        </div>
                                        <div className="calculation-row refund-amount"><span className="calculation-label">{translations?.refundAmount || 'Refund Amount'}:</span><span className="calculation-value highlight" style={{ color: '#dc2626' }}>Rs. {formData.refund_amount}</span></div>
                                    </div>
                                </div>

                                <div className="form-actions-modern">
                                    <button type="button" className="btn-modern btn-secondary-modern" onClick={handleClear} disabled={saving}><FontAwesomeIcon icon={faTimes} /><span>{translations?.clear || 'Clear'}</span></button>
                                    <button type="submit" className="btn-modern btn-primary-modern" disabled={saving || isTranslating} style={{ background: 'linear-gradient(135deg, #dc2626, #991b1b)' }}>
                                        {saving ? <><div className="btn-spinner"></div><span>{translations?.saving || 'Processing...'}</span></> : <><span>{translations?.processReturnBtn || 'Process Return'}</span><FontAwesomeIcon icon={faUndo} /></>}
                                    </button>
                                </div>
                            </>
                        )}
                    </>
                )}
            </form>

            {loading && <div className="loading-overlay"><div className="loading-content"><div className="loading-spinner"></div><p>{translations?.loading || 'Loading...'}</p></div></div>}
        </div>
    );
};

export default FabReturn;